import { MEETING_ROOM_ENDPOINT_PATH } from './constants.js';
import type { ReservableRoom } from './types.js';

const buildAvailabilityMap = (reservableRooms: ReservableRoom[]) => {
  const availabilityByShortName = new Map<string, ReservableRoom>();
  for (const room of reservableRooms) {
    availabilityByShortName.set(room.value, room);
  }
  return availabilityByShortName;
};

export const resolveMeetingRoomAvailabilityUrl = () => {
  const baseUrl = import.meta.env.VITE_MEETING_ROOM_AVAILABILITY_API_URL;

  if (!baseUrl) {
    throw new Error('VITE_MEETING_ROOM_AVAILABILITY_API_URL is not defined');
  }

  const trimmedBase = baseUrl.replace(/\/$/, '');

  try {
    const parsed = new URL(trimmedBase);
    const basePath = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');

    if (import.meta.env.DEV && parsed.origin !== window.location.origin) {
      const proxyPath = `${basePath}${MEETING_ROOM_ENDPOINT_PATH}`;
      return proxyPath.startsWith('/') ? proxyPath : `/${proxyPath}`;
    }

    return `${parsed.origin}${basePath}${MEETING_ROOM_ENDPOINT_PATH}`;
  } catch {
    if (trimmedBase === '') {
      return MEETING_ROOM_ENDPOINT_PATH;
    }
    const sanitizedBase = trimmedBase.startsWith('/') ? trimmedBase : `/${trimmedBase}`;
    return `${sanitizedBase}${MEETING_ROOM_ENDPOINT_PATH}`;
  }
};

export const fetchAvailabilitySnapshot = async (apiKey: string, building?: string | null) => {
  const baseUrl = resolveMeetingRoomAvailabilityUrl();

  const resolvedBuilding = building ?? undefined;
  const url = resolvedBuilding
    ? `${baseUrl}?building=${encodeURIComponent(resolvedBuilding)}`
    : baseUrl;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: apiKey,
    },
  });

  if (!response.ok) {
    throw new Error(`Response status: ${response.status}`);
  }

  const reservableRooms: ReservableRoom[] = await response.json();
  return buildAvailabilityMap(reservableRooms);
};
